import type { OnChanges, WritableSignal } from '@angular/core';
import {
  Component,
  DestroyRef,
  Injector,
  computed,
  effect,
  inject,
  input,
  isSignal,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AbstractControl } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { chain, isArray, isEqual } from 'lodash';
import type { Observable } from 'rxjs';
import type { OnyxMaybeArray, OnyxSuggestion } from '../../../interfaces';

@Component({
  selector: 'onyx-suggestions',
  imports: [TranslatePipe],
  templateUrl: './onyx-suggestions.component.html',
  styleUrl: './onyx-suggestions.component.scss',
})
export class OnyxSuggestionsComponent<T> implements OnChanges {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  public readonly control = input.required<
    AbstractControl | WritableSignal<OnyxMaybeArray<T>>
  >();
  public readonly suggestions = input.required<OnyxSuggestion<T>[] | null>();
  public readonly multiple = input(false);
  public readonly disabled = input(false);

  public readonly selectedSuggestionChange = output<T>();

  protected suggestionsData = computed(() => {
    const value = this.value();
    if (!this.suggestions()) return;

    return this.suggestions()!.map((suggestion) => ({
      ...suggestion,
      disabled:
        this.disabled_() ||
        (isArray(value)
          ? value.includes(suggestion.value)
          : isEqual(value, suggestion.value)),
    }));
  });

  private value_ = signal<T | null>(null);
  protected get value() {
    return this.value_.asReadonly();
  }

  private disabled_ = signal(false);

  public ngOnChanges(): void {
    effect(
      () => {
        this.update();

        const control = this.control();
        if (isSignal(control)) return;

        const statusChanges$ = (
          control.statusChanges as Observable<any> | undefined
        )?.pipe(takeUntilDestroyed(this.destroyRef));

        const valueChanges$ = (
          control.valueChanges as Observable<any> | undefined
        )?.pipe(takeUntilDestroyed(this.destroyRef));

        statusChanges$?.subscribe(() => this.update());
        valueChanges$?.subscribe(() => this.update());
      },
      { injector: this.injector },
    );

    effect(() => (isSignal(this.control()) ? this.update() : null), {
      injector: this.injector,
    });
  }

  protected setValue(value: T): void {
    const control = this.control();
    const updatedValue = this.multiple()
      ? chain(isSignal(control) ? control() : control.value)
          .defaultTo([])
          .concat(value)
          .value()
      : value;

    isSignal(control)
      ? control.set(updatedValue as T)
      : control.setValue(updatedValue);
    this.selectedSuggestionChange.emit(value);
  }

  private update(): void {
    const control = this.control();
    const value = isSignal(control) ? control() : control.value;
    const disabled = isSignal(control) ? this.disabled() : control.disabled;

    this.value_.set(value);
    this.disabled_.set(disabled);
  }
}
